/* ==========================================================================
   BeBox — Main JavaScript
   Nav behavior, accordion, smooth scroll, count-up,
   waitlist form handling, scroll-driven product reveal
   ========================================================================== */

(function () {
  'use strict';

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ------------------------------------------------------------------------
     Shopify spam protection and waitlist forms
     Shopify's captcha-bootstrap (injected by content_for_header) owns the
     submission of /contact customer forms: it cancels the submit event and
     posts the form itself once hCaptcha is bound to it. hCaptcha binds on the
     first focus, and a submit that arrives before binding finishes is
     cancelled and never replayed. guardWaitlistSubmit() replays that one
     submit once the form is bound, and releases the form's loading state if
     the visitor is still on the page after a while (e.g. a visible
     challenge). Call it from a form's submit handler after validation passes.
     ------------------------------------------------------------------------ */
  var pageLeaving = false;
  window.addEventListener('pagehide', function () { pageLeaving = true; });

  function isCaptchaBound(form) {
    return form.hasAttribute('data-hcaptcha-bound') || form.hasAttribute('data-recaptcha-bound');
  }

  function guardWaitlistSubmit(form, event, unlock) {
    // Runs after the event has finished dispatching, so defaultPrevented
    // reflects Shopify's captcha listener on the document.
    setTimeout(function () {
      if (pageLeaving) return;
      if (event.defaultPrevented && !isCaptchaBound(form) && !form.__beboxReplayArmed && window.MutationObserver) {
        form.__beboxReplayArmed = true;
        var observer = new MutationObserver(function () {
          if (!isCaptchaBound(form)) return;
          observer.disconnect();
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        });
        observer.observe(form, { attributes: true });
        setTimeout(function () { observer.disconnect(); }, 15000);
      }
      setTimeout(function () {
        if (!pageLeaving && typeof unlock === 'function') unlock();
      }, 12000);
    }, 0);
  }

  window.beboxGuardWaitlistSubmit = guardWaitlistSubmit;

  /* ========================================================================
     Nav Scroll Behavior (background opacity transition)
     ======================================================================== */

  function initNavScroll() {
    var header = document.getElementById('site-header');
    if (!header) return;

    var scrollThreshold = window.innerHeight * 0.5;
    var ticking = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        if (window.scrollY > scrollThreshold) {
          header.classList.add('is-scrolled');
        } else {
          header.classList.remove('is-scrolled');
        }
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ========================================================================
     Mobile Menu
     ======================================================================== */

  function initMobileMenu() {
    var hamburger = document.querySelector('.site-header__hamburger');
    var menu = document.getElementById('mobile-menu');
    var closeBtn = document.querySelector('.mobile-menu__close');

    if (!hamburger || !menu) return;

    var savedScrollY = 0;

    // A closed menu is invisible, so keep it out of the tab order and the
    // accessibility tree (inert) until it opens.
    menu.inert = true;

    function lockScroll() {
      savedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    }

    function unlockScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      // Restore instantly: article pages set html { scroll-behavior: smooth },
      // which would otherwise animate from the top back down to the reader.
      var root = document.documentElement;
      var previous = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, savedScrollY);
      root.style.scrollBehavior = previous;
    }

    function openMenu() {
      menu.inert = false;
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      hamburger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('mobile-menu-open');
      lockScroll();
      if (closeBtn) closeBtn.focus();
    }

    function closeMenu() {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      menu.inert = true;
      hamburger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('mobile-menu-open');
      unlockScroll();
      hamburger.focus();
    }

    hamburger.addEventListener('click', function () {
      var isOpen = menu.classList.contains('is-open');
      if (isOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', closeMenu);
    }

    menu.addEventListener('click', function (e) {
      if (e.target === menu || e.target.classList.contains('mobile-menu__panel')) closeMenu();
    });

    var mobileLinks = menu.querySelectorAll('.mobile-menu__link, .mobile-menu__cta');
    mobileLinks.forEach(function (link) {
      link.addEventListener('click', closeMenu);
    });

    function closeMenuIfDesktop() {
      if (!menu.classList.contains('is-open')) return;
      var isHamburgerVisible = window.getComputedStyle(hamburger).display !== 'none';
      if (!isHamburgerVisible) closeMenu();
    }

    var desktopMq = window.matchMedia('(min-width: 769px)');
    desktopMq.addEventListener('change', closeMenuIfDesktop);

    window.matchMedia('(max-height: 500px) and (max-width: 1024px)')
      .addEventListener('change', closeMenuIfDesktop);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) {
        closeMenu();
      }
      if (e.key === 'Tab' && menu.classList.contains('is-open')) {
        var focusable = menu.querySelectorAll(
          'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  /* ========================================================================
     Header "More" Dropdown (desktop)
     ======================================================================== */

  function initHeaderDropdown() {
    var dropdowns = document.querySelectorAll('.site-header__dropdown');
    if (!dropdowns.length) return;

    dropdowns.forEach(function (dd) {
      var trigger = dd.querySelector('.site-header__dropdown-trigger');
      var panel = dd.querySelector('.site-header__dropdown-panel');
      if (!trigger || !panel) return;

      var hoverTimeout;

      function open() {
        trigger.setAttribute('aria-expanded', 'true');
        dd.classList.add('is-open');
      }
      function close() {
        trigger.setAttribute('aria-expanded', 'false');
        dd.classList.remove('is-open');
      }

      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (dd.classList.contains('is-open')) { close(); } else { open(); }
      });

      document.addEventListener('click', function (e) {
        if (!dd.contains(e.target)) close();
      });

      dd.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { close(); trigger.focus(); }
      });

      /* Close when keyboard focus leaves the dropdown entirely */
      dd.addEventListener('focusout', function (e) {
        if (!dd.contains(e.relatedTarget)) close();
      });

      dd.addEventListener('mouseenter', function () {
        clearTimeout(hoverTimeout);
        open();
      });
      dd.addEventListener('mouseleave', function () {
        hoverTimeout = setTimeout(close, 200);
      });
    });
  }

  /* ========================================================================
     Smooth Scroll for Anchor Links
     ======================================================================== */

  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;

      var targetId = link.getAttribute('href');
      if (targetId === '#') return;
      if (!/^#[\w-]+$/.test(targetId)) return;

      var target;
      try {
        target = document.querySelector(targetId);
      } catch (err) { return; }
      if (!target) return;

      e.preventDefault();

      var header = document.getElementById('site-header');
      var headerHeight = header ? header.offsetHeight : 64;
      var targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight;

      window.scrollTo({
        top: targetPosition,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });

      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  }

  /* ========================================================================
     FAQ Accordion
     ======================================================================== */

  function initAccordion() {
    /* Skip items inside .faq-page__accordion-group — those have their own
       category-scoped handlers in page-faq.liquid */
    var items = document.querySelectorAll('.accordion__item:not(.faq-page__accordion-group .accordion__item)');
    if (!items.length) return;

    items.forEach(function (item) {
      var trigger = item.querySelector('.faq__question');
      var content = item.querySelector('.accordion__content');
      if (!trigger || !content) return;

      trigger.addEventListener('click', function () {
        var isActive = item.classList.contains('is-active');

        items.forEach(function (otherItem) {
          otherItem.classList.remove('is-active');
          var otherTrigger = otherItem.querySelector('.faq__question');
          var otherContent = otherItem.querySelector('.accordion__content');
          if (otherTrigger) {
            otherTrigger.setAttribute('aria-expanded', 'false');
          }
          if (otherContent) {
            otherContent.setAttribute('aria-hidden', 'true');
          }
        });

        if (!isActive) {
          item.classList.add('is-active');
          trigger.setAttribute('aria-expanded', 'true');
          content.setAttribute('aria-hidden', 'false');
        }
      });
    });
  }

  /* ========================================================================
     Count-Up Animation
     ======================================================================== */

  function initCountUp() {
    var counters = document.querySelectorAll('.count-up');
    if (!counters.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animateCount(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach(function (el) {
      observer.observe(el);
    });
  }

  function animateCount(el) {
    var target = parseInt(el.getAttribute('data-target'), 10);
    if (isNaN(target)) return;

    var prefix = el.getAttribute('data-prefix') || '';
    var suffix = el.getAttribute('data-suffix') || '';

    // Leave the server-rendered value alone when counting would mislead or
    // add motion the visitor opted out of: ranges ("6–12", "$400–$675") would
    // read "0–12" mid-count, and single digits barely move.
    if (prefersReducedMotion() || /^\s*[–—-]\s*\$?\d/.test(suffix) || target < 10) return;

    var duration = 1500;
    var start = 0;
    var startTime = null;

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var progress = Math.min((timestamp - startTime) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = Math.floor(eased * (target - start) + start);

      el.textContent = prefix + current + suffix;

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        el.textContent = prefix + target + suffix;
      }
    }

    requestAnimationFrame(step);
  }

  /* ========================================================================
     Waitlist Form Handling
     ======================================================================== */

  function initWaitlistForms() {
    var footerForm = document.getElementById('footer-waitlist-form');
    var footerWrap = document.getElementById('footer-waitlist-wrap');
    var footerSuccess = document.getElementById('footer-waitlist-success');

    if (footerForm) {
      footerForm.addEventListener('submit', function (e) {
        var btn = footerForm.querySelector('button[type="submit"]');
        if (btn) {
          btn.disabled = true;
          btn.classList.add('is-loading');
        }
        guardWaitlistSubmit(footerForm, e, function () {
          if (btn) {
            btn.disabled = false;
            btn.classList.remove('is-loading');
          }
        });
      });
    }

    // Shopify sends the visitor back with ?customer_posted=true. The form
    // action is /contact#<form-id>, so the fragment tells us which form on the
    // page was submitted. Unknown fragments keep the old behaviour.
    var urlParams = new URLSearchParams(window.location.search);
    var posted = urlParams.get('customer_posted') === 'true';
    var postedFormId = posted ? window.location.hash.slice(1) : '';
    var knownForms = ['waitlist-form', 'footer-waitlist-form', 'blog-waitlist-form', 'page-waitlist-form'];
    var postedFromKnownForm = knownForms.indexOf(postedFormId) !== -1;

    if (posted && footerWrap && footerSuccess) {
      var footerPosted = postedFormId === 'footer-waitlist-form' ||
        (!postedFromKnownForm && !document.getElementById('waitlist'));
      if (footerPosted) {
        footerWrap.style.display = 'none';
        footerSuccess.hidden = false;
        // The fragment target is now hidden, so bring the confirmation into view.
        footerSuccess.scrollIntoView({ block: 'center' });
      }
    }

    var formWrap = document.getElementById('waitlist-form-wrap');
    var form = document.getElementById('waitlist-form');
    var successEl = document.getElementById('waitlist-success');
    var statusEl = document.getElementById('waitlist-status');

    if (!form || !formWrap || !successEl) return;

    var submitBtn = form.querySelector('.waitlist__submit');
    var emailInput = form.querySelector('.waitlist__input');
    var errorEl = document.getElementById('waitlist-error');

    var stringsEl = document.getElementById('waitlist-strings');
    var i18n = {
      submit: stringsEl ? stringsEl.getAttribute('data-submit') : 'Join Waitlist',
      submitting: stringsEl ? stringsEl.getAttribute('data-submitting') : 'Submitting\u2026',
      success: stringsEl ? stringsEl.getAttribute('data-success') : 'You\u2019re on the list.',
      error: stringsEl ? stringsEl.getAttribute('data-error') : 'Please check your email and try again.'
    };

    form.addEventListener('submit', function (e) {
      if (!emailInput || !submitBtn) return;

      var email = emailInput.value.trim();
      var atIdx = email.indexOf('@');
      if (!email || atIdx < 1 || email.indexOf('.', atIdx) < 0) {
        e.preventDefault();
        emailInput.classList.add('is-error');
        if (errorEl) errorEl.textContent = i18n.error;
        emailInput.focus();
        return;
      }

      if (emailInput) emailInput.classList.remove('is-error');
      if (errorEl) errorEl.textContent = '';

      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
      submitBtn.textContent = submitBtn.getAttribute('data-loading-text') || i18n.submitting;
      // readOnly, not disabled: disabled fields are left out of the POST,
      // which is how this form used to submit without the email address.
      if (emailInput) emailInput.readOnly = true;

      if (statusEl) statusEl.textContent = i18n.submitting;

      guardWaitlistSubmit(form, e, function () {
        emailInput.readOnly = false;
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-loading');
        submitBtn.textContent = submitBtn.getAttribute('data-default-text') || i18n.submit;
        if (statusEl) statusEl.textContent = '';
      });
    });

    var isSuccess = posted && (postedFormId === 'waitlist-form' || !postedFromKnownForm);

    if (isSuccess && formWrap.closest('#waitlist')) {
      showSuccess();
    }

    if (errorEl && errorEl.textContent.trim()) {
      showError();
    }

    function showSuccess() {
      formWrap.classList.add('is-hidden');

      var transitionFired = false;
      function onFadeOut() {
        if (transitionFired) return;
        transitionFired = true;
        formWrap.removeEventListener('transitionend', onFadeOut);
        formWrap.style.display = 'none';
        successEl.hidden = false;

        void successEl.offsetHeight;
        successEl.classList.add('is-visible');

        if (statusEl) statusEl.textContent = i18n.success;
      }

      formWrap.addEventListener('transitionend', onFadeOut);
      setTimeout(onFadeOut, 350);
    }

    function showError() {
      if (emailInput) {
        emailInput.classList.add('is-error');
        emailInput.readOnly = false;
        emailInput.focus();
      }
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-loading');
        submitBtn.textContent = submitBtn.getAttribute('data-default-text') || i18n.submit;
      }
      if (statusEl) statusEl.textContent = i18n.error;
    }
  }

  /* ========================================================================
     Scroll-Driven Product Reveal (frame scrubber)
     Maps scroll position within a sticky section to frame index.
     Not scroll-jacking — user controls scroll naturally.
     ======================================================================== */

  function initProductReveal() {
    var canvas = document.getElementById('product-reveal-canvas');
    if (!canvas) return;

    var section = canvas.closest('.product-reveal');
    if (!section) return;

    var ctx = canvas.getContext('2d');
    var frameCount = parseInt(canvas.getAttribute('data-frame-count'), 10) || 121;
    var basePath = canvas.getAttribute('data-frame-base') || '';
    var frameList = null;
    try {
      var listEl = document.getElementById('product-reveal-frames');
      if (listEl) frameList = JSON.parse(listEl.textContent);
    } catch (err) { frameList = null; }
    if (frameList && frameList.length) frameCount = Math.min(frameCount, frameList.length);
    var frames = new Array(frameCount);
    var currentFrame = -1;

    var annotations = section.querySelectorAll('.annotation-card');
    var placeholder = document.getElementById('product-reveal-placeholder');

    // Versioned URLs come from Liquid (asset_url keeps ?v=), so re-exported
    // frames are never served stale and frame 1 reuses the placeholder download.
    function frameUrl(i) {
      if (frameList && frameList[i - 1]) return frameList[i - 1];
      return basePath + 'frame_' + String(i).padStart(4, '0') + '.jpg';
    }

    function loadFrame(i) {
      if (frames[i - 1]) return;
      var img = new Image();
      img.decoding = 'async';
      img.addEventListener('load', onScroll);
      img.src = frameUrl(i);
      frames[i - 1] = img;
    }

    // Reduced-motion and data-saver visitors keep the static placeholder:
    // no frame downloads, no scrubbing.
    var connection = navigator.connection || {};
    var staticOnly = prefersReducedMotion() || connection.saveData === true;

    // Frames load only once the section is about a screen away and the page
    // itself has finished loading, so they never compete with the hero.
    // Every 4th frame first (scrubbing works early), then the rest.
    function loadAllFrames() {
      var i;
      for (i = 1; i <= frameCount; i += 4) loadFrame(i);
      for (i = 1; i <= frameCount; i++) loadFrame(i);
    }

    function whenPageLoaded(fn) {
      if (document.readyState === 'complete') { fn(); return; }
      window.addEventListener('load', fn, { once: true });
    }

    if (!staticOnly) {
      var preloadObserver = new IntersectionObserver(function(entries) {
        if (!entries[0].isIntersecting) return;
        preloadObserver.disconnect();
        whenPageLoaded(function () {
          loadFrame(1);
          if (frames[0].complete) {
            onFirstFrame();
          } else {
            frames[0].addEventListener('load', onFirstFrame, { once: true });
          }
          loadAllFrames();
        });
      }, { rootMargin: '100% 0px' });

      // Arm the look-ahead on the first scroll: on tall desktop and tablet
      // screens the section is already within one screen at load, and a visitor
      // who never scrolls should not download ~3.9 MB of frames.
      var armPreload = function () { preloadObserver.observe(section); };
      if (window.scrollY > 0) {
        armPreload();
      } else {
        window.addEventListener('scroll', armPreload, { once: true, passive: true });
      }
    }

    // Resize canvas to fill viewport (cover fit)
    function resizeCanvas() {
      var dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      currentFrame = -1; // force redraw
    }

    resizeCanvas();
    var resizeTimer;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimer);
      // Resizing clears the canvas, so repaint the current frame straight away.
      resizeTimer = setTimeout(function () { resizeCanvas(); onScroll(); }, 150);
    });

    // Hand over from the placeholder once the canvas has something to show.
    function onFirstFrame() {
      currentFrame = -1;
      onScroll();
      if (placeholder) placeholder.classList.add('is-hidden');
    }

    function isReady(img) {
      return !!(img && img.complete && img.naturalWidth);
    }

    // While frames are still arriving, show the closest one that has loaded.
    function nearestLoaded(index) {
      for (var d = 0; d < frameCount; d++) {
        if (isReady(frames[index - d])) return index - d;
        if (isReady(frames[index + d])) return index + d;
      }
      return -1;
    }

    function drawFrame(requested) {
      var index = isReady(frames[requested]) ? requested : nearestLoaded(requested);
      if (index < 0 || index === currentFrame) return;
      currentFrame = index;

      var dpr = window.devicePixelRatio || 1;
      var cw = canvas.width;
      var ch = canvas.height;
      var iw = frames[index].naturalWidth;
      var ih = frames[index].naturalHeight;

      ctx.clearRect(0, 0, cw, ch);

      // Cover fit: scale image to fill canvas, center crop
      var scale = Math.max(cw / iw, ch / ih);
      var drawW = iw * scale;
      var drawH = ih * scale;
      var dx = (cw - drawW) / 2;
      var dy = (ch - drawH) / 2;

      ctx.drawImage(frames[index], dx, dy, drawW, drawH);
    }

    // Pre-cache annotation thresholds to avoid per-frame DOM reads
    var annotationData = [];
    annotations.forEach(function(card) {
      annotationData.push({
        el: card,
        show: parseFloat(card.getAttribute('data-show')) / 100,
        hide: parseFloat(card.getAttribute('data-hide')) / 100,
        visible: false
      });
    });

    function updateAnnotations(progress) {
      for (var i = 0; i < annotationData.length; i++) {
        var a = annotationData[i];
        var shouldBeVisible = progress >= a.show && progress <= a.hide;
        if (shouldBeVisible !== a.visible) {
          a.visible = shouldBeVisible;
          if (shouldBeVisible) {
            a.el.classList.add('is-visible');
            a.el.setAttribute('aria-hidden', 'false');
          } else {
            a.el.classList.remove('is-visible');
            a.el.setAttribute('aria-hidden', 'true');
          }
        }
      }
    }

    var ticking = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(function() {
        var rect = section.getBoundingClientRect();
        var sectionHeight = section.offsetHeight;
        var viewH = window.innerHeight;

        var denominator = sectionHeight - viewH;
        var scrollProgress = denominator > 0 ? (-rect.top) / denominator : 0;
        scrollProgress = Math.max(0, Math.min(1, scrollProgress));

        var frameIndex = Math.min(
          frameCount - 1,
          Math.floor(scrollProgress * frameCount)
        );

        drawFrame(frameIndex);
        updateAnnotations(scrollProgress);
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ========================================================================
     GA4 Event Tracking — Waitlist Conversion Funnel
     Uses gtag() directly (not dataLayer.push) to match the gtag.js tag
     installed in theme.liquid. Silent when gtag is absent.
     ======================================================================== */

  function initAnalytics() {
    if (typeof gtag !== 'function') return;

    function track(name, params) {
      gtag('event', name, params || {});
    }

    function hashEmail(email) {
      var normalized = email.toLowerCase().trim();
      var buf = new TextEncoder().encode(normalized);
      return crypto.subtle.digest('SHA-256', buf).then(function (hashBuf) {
        return Array.from(new Uint8Array(hashBuf))
          .map(function (b) { return b.toString(16).padStart(2, '0'); })
          .join('');
      });
    }

    // -----------------------------------------------------------------
    // 1. Waitlist signups (THE key conversion)
    //    Counted once, when Shopify confirms the signup: the visitor comes
    //    back to ?customer_posted=true#<form-id>. The submit event is not a
    //    reliable signal because Shopify's spam protection cancels it and
    //    posts the form itself. The email is hashed while the visitor types
    //    and kept in sessionStorage for enhanced conversions on return.
    //    Fires generate_lead (GA4 recommended) and waitlist_signup (custom,
    //    for backwards compat).
    // -----------------------------------------------------------------
    var LEAD_HASH_KEY = 'bebox_lead_email_sha256';
    var hashTimer = null;

    document.addEventListener('input', function (e) {
      var input = e.target;
      if (!input.matches || !input.matches('form input[type="email"]')) return;
      if (!window.crypto || !window.crypto.subtle) return;
      clearTimeout(hashTimer);
      hashTimer = setTimeout(function () {
        var value = input.value.trim().toLowerCase();
        if (!value) return;
        hashEmail(value).then(function (hashed) {
          try { sessionStorage.setItem(LEAD_HASH_KEY, hashed); } catch (err) {}
        }).catch(function () {});
      }, 250);
    });

    (function trackConfirmedSignup() {
      var params = new URLSearchParams(window.location.search);
      if (params.get('customer_posted') !== 'true') return;

      var formLocation = window.location.hash.slice(1) || 'unknown';
      var hashed = null;
      try {
        hashed = sessionStorage.getItem(LEAD_HASH_KEY);
        sessionStorage.removeItem(LEAD_HASH_KEY);
      } catch (err) {}

      if (hashed) gtag('set', 'user_data', { sha256_email_address: hashed });
      track('generate_lead', { form_location: formLocation, currency: 'USD', value: 0 });
      track('waitlist_signup', { form_location: formLocation });

      // Drop the confirmation from the URL (after every success message has
      // read it) so a refresh or a shared link does not count it again.
      params.delete('customer_posted');
      var query = params.toString();
      window.history.replaceState(window.history.state, document.title,
        window.location.pathname + (query ? '?' + query : ''));
    })();

    // -----------------------------------------------------------------
    // 2. Waitlist form start (focus on email input = intent signal)
    // -----------------------------------------------------------------
    var formStartFired = {};

    document.addEventListener('focusin', function (e) {
      var input = e.target.closest(
        '#waitlist-email, #coming-soon-email, #footer-waitlist-email, ' +
        'form input[type="email"]'
      );
      if (!input) return;

      var form = input.closest('form');
      var formId = form ? form.id || 'unknown' : 'unknown';
      if (formStartFired[formId]) return;
      formStartFired[formId] = true;

      track('waitlist_form_start', { form_location: formId });
    });

    // -----------------------------------------------------------------
    // 3. Form validation errors (friction signal)
    // -----------------------------------------------------------------
    document.addEventListener('invalid', function (e) {
      var input = e.target;
      var form = input.closest('form');
      if (!form) return;

      var tagsInput = form.querySelector('input[name="contact[tags]"]');
      var isWaitlist = tagsInput && tagsInput.value.indexOf('waitlist') !== -1;
      if (!isWaitlist) return;

      track('form_error', {
        form_location: form.id || 'unknown',
        error_field: input.name || input.type,
        error_type: input.validationMessage || 'validation_failed'
      });
    }, true);

    // -----------------------------------------------------------------
    // 4. CTA clicks (all primary/secondary buttons)
    // -----------------------------------------------------------------
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.btn--primary, .btn--secondary');
      if (!btn) return;

      track('cta_click', {
        cta_text: btn.textContent.trim(),
        cta_url: btn.getAttribute('href') || '',
        cta_type: btn.classList.contains('btn--primary') ? 'primary' : 'secondary',
        cta_section: (btn.closest('section') || {}).id || 'unknown'
      });
    });

    // -----------------------------------------------------------------
    // 5. Nav link clicks (which pages/sections interest users)
    // -----------------------------------------------------------------
    document.addEventListener('click', function (e) {
      var link = e.target.closest('.site-header__link, .mobile-menu__link, .mobile-menu__cta');
      if (!link) return;

      track('nav_click', {
        link_text: link.textContent.trim(),
        link_url: link.getAttribute('href') || '',
        nav_type: link.closest('.mobile-menu') ? 'mobile' : 'desktop'
      });
    });

    // -----------------------------------------------------------------
    // 6. Mobile menu opens (mobile engagement signal)
    // -----------------------------------------------------------------
    var hamburger = document.querySelector('.site-header__hamburger');
    if (hamburger) {
      hamburger.addEventListener('click', function () {
        var menu = document.getElementById('mobile-menu');
        if (menu && !menu.classList.contains('is-open')) {
          track('mobile_menu_open');
        }
      });
    }

    // -----------------------------------------------------------------
    // 7. Scroll depth milestones (25/50/75/100%)
    // -----------------------------------------------------------------
    var milestones = { 25: false, 50: false, 75: false, 100: false };
    var scrollRafPending = false;

    window.addEventListener('scroll', function () {
      if (scrollRafPending) return;
      scrollRafPending = true;
      requestAnimationFrame(function () {
        scrollRafPending = false;
        var docHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight <= 0) return;
        var pct = Math.round((window.scrollY / docHeight) * 100);

        for (var m in milestones) {
          if (!milestones[m] && pct >= parseInt(m, 10)) {
            milestones[m] = true;
            track('scroll_depth', { percent: parseInt(m, 10) });
          }
        }
      });
    }, { passive: true });

    // -----------------------------------------------------------------
    // 8. Section views (IntersectionObserver — fires once per section)
    //    Shows which content users actually see vs scroll past.
    // -----------------------------------------------------------------
    var trackedSections = [
      'hero', 'product-reveal', 'features', 'how-it-works',
      'proof', 'specs', 'faq', 'waitlist', 'reviews'
    ];
    var sectionViewed = {};

    if ('IntersectionObserver' in window) {
      var sectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var id = entry.target.id;
          if (sectionViewed[id]) return;
          sectionViewed[id] = true;
          track('section_view', {
            section_name: id,
            section_index: trackedSections.indexOf(id)
          });
          sectionObserver.unobserve(entry.target);
        });
      }, { threshold: 0.3 });

      trackedSections.forEach(function (id) {
        var el = document.getElementById(id);
        if (el) sectionObserver.observe(el);
      });
    }

    // -----------------------------------------------------------------
    // 9. FAQ accordion opens (which objections users research)
    // -----------------------------------------------------------------
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('.faq__question');
      if (!trigger) return;

      var item = trigger.closest('.accordion__item');
      if (!item || item.classList.contains('is-active')) return;

      track('faq_open', {
        question: trigger.textContent.trim().substring(0, 100),
        faq_section: (trigger.closest('section') || {}).id || 'inline'
      });
    });

    // -----------------------------------------------------------------
    // 10. Engaged time milestones (quality of attention)
    //     Pauses when tab is hidden to avoid inflated numbers.
    // -----------------------------------------------------------------
    var timeGoals = [30, 60, 120];
    var elapsedSeconds = 0;
    var timeTimer = null;

    function startTimer() {
      if (timeTimer) return;
      timeTimer = setInterval(function () {
        elapsedSeconds++;
        for (var i = timeGoals.length - 1; i >= 0; i--) {
          if (elapsedSeconds === timeGoals[i]) {
            track('engaged_time', { duration_seconds: timeGoals[i] });
            timeGoals.splice(i, 1);
          }
        }
        if (!timeGoals.length) clearInterval(timeTimer);
      }, 1000);
    }

    function stopTimer() {
      if (timeTimer) { clearInterval(timeTimer); timeTimer = null; }
    }

    if (!document.hidden) startTimer();
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopTimer(); else startTimer();
    });
  }

  /* ========================================================================
     Link Prefetching — preload pages on hover/touch for instant navigation
     ======================================================================== */

  function initPrefetch() {
    var prefetched = {};
    var hoverTimer = null;

    function prefetchUrl(url) {
      if (prefetched[url]) return;
      prefetched[url] = true;
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);
    }

    function getEligibleHref(anchor) {
      if (!anchor || !anchor.href) return null;
      try {
        var url = new URL(anchor.href, location.origin);
      } catch (e) { return null; }
      if (url.origin !== location.origin) return null;
      if (url.pathname === location.pathname) return null;
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
      return url.href;
    }

    document.addEventListener('mouseover', function (e) {
      var anchor = e.target.closest('a');
      var href = getEligibleHref(anchor);
      if (!href) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(function () { prefetchUrl(href); }, 65);
    });

    document.addEventListener('mouseout', function (e) {
      if (e.target.closest('a')) clearTimeout(hoverTimer);
    });

    document.addEventListener('touchstart', function (e) {
      var anchor = e.target.closest('a');
      var href = getEligibleHref(anchor);
      if (href) prefetchUrl(href);
    }, { passive: true });
  }

  /* ========================================================================
     Initialize Everything
     ======================================================================== */

  function init() {
    initNavScroll();
    initMobileMenu();
    initHeaderDropdown();
    initSmoothScroll();
    initAccordion();
    initCountUp();
    initWaitlistForms();
    initProductReveal();
    initPrefetch();
    initAnalytics();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
